import {
  Typography,
  Grid,
  Theme,
  makeStyles,
  createStyles,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  Tooltip,
  Button,
  Box,
  Paper,
  Tabs,
  Tab,
  useMediaQuery,
} from '@material-ui/core';
import React, { useState } from 'react';
import { Maybe, PayerTransaction, PaymentMethod, PaymentMethodType, PaymentStatus } from '../gql-types.generated';
import CloseIcon from '@material-ui/icons/Close';
import CopyIcon from '@material-ui/icons/FileCopyOutlined';
import { paymentStatusDisplay } from '../util/PaymentStatus';
import TabPanel from './TabPanel';

const useStylesDesktop = makeStyles((theme: Theme) =>
  createStyles({
    dialogContent: {
      padding: theme.spacing(0, 0, 4, 0),
    },
    infoSections: {
      margin: 0,
      padding: theme.spacing(1.5),
    },
    contentSectionMd: {
      margin: 0,
      padding: theme.spacing(0, 1.5, 0, 2),
      marginRight: 48,
    },
    contentSectionSm: {
      margin: 0,
      padding: theme.spacing(0, 1.5, 0, 2),
      marginRight: 36,
    },
    referenceId: {
      padding: 0,
      margin: 0,
    },
    referenceContent: {
      fontWeight: 'bold',
      margin: 0,
      wordWrap: 'break-word',
      wordBreak: 'break-all',
    },
    closeButtonContainer: {
      textAlign: 'right',
    },
    closeButton: {
      color: theme.palette.grey[500],
    },
    selectedTab: {
      backgroundColor: '#EEF4FE',
    },
    tabRoot: {
      padding: theme.spacing(0, 6),
      minWidth: '50%',
    },
    tabsIndicator: {
      height: 0,
      width: 0,
    },
    tabsRoot: {
      borderBottom: 'solid 3px #0275D8',
    },
    headingContent: {
      color: 'grey',
    },
    detailsContent: {
      color: 'black',
    },
    copyIcon: {
      fontSize: '1rem',
      marginLeft: 15,
    },
    table: {
      width: '100%',
      minHeight: '250px',
    },
    tooltip: {
      cursor: 'pointer',
      paddingBottom: 0,
    },
    cell: {
      color: theme.palette.grey[800],
    },
    headerCell: {
      color: theme.palette.grey[800],
      backgroundColor: '#FFFFFF',
    },
    paymentButton: {
      padding: theme.spacing(1, 0),
      marginBottom: theme.spacing(1),
    },
    actionButtonsContainerSm: {
      paddingLeft: 32,
      paddingTop: theme.spacing(1),
    },
    dialogWidth: {
      minWidth: 700,
      maxWidth: 700,
    },
  }),
);

interface RecentPaymentDetailsProps {
  recentPaymentDetailsVisible: boolean;
  payment: PayerTransaction | null | undefined;
  closeRecentPaymentDetails: () => void;
  creditMemosEnabled: boolean;
}

const RecentPaymentDetails: React.FC<RecentPaymentDetailsProps> = props => {
  const classesDesktop = useStylesDesktop();
  const { recentPaymentDetailsVisible, payment, closeRecentPaymentDetails, creditMemosEnabled } = props;
  const paymentsHistoryCount = (payment?.paymentRequest?.payments.length || 0) > 0;
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const [referenceCopied, setReferenceCopied] = useState(false);
  const [tabValue] = useState(0);

  const getPaymentMethodString = (
    paymentMethod: Maybe<PaymentMethod> | undefined,
    amount: Maybe<number> | undefined,
    creditAmount: Maybe<number> | undefined,
  ): string => {
    if (!amount && creditAmount) return 'None';
    switch (paymentMethod?.type) {
      case PaymentMethodType.CreditCard:
        return `${paymentMethod?.creditCard?.cardBrand} ${paymentMethod?.creditCard?.lastFour}`;
      case PaymentMethodType.PaymentBankUs:
        return `Bank ${paymentMethod?.paymentBank?.lastFour}`;
      default:
        return 'None';
    }
  };

  const paymentHistory = () => {
    let remainingBalance = 0;
    if (payment?.paymentRequest?.amount) {
      remainingBalance = payment.paymentRequest.amount;
    }

    if (
      payment?.paymentRequest?.discountAmount &&
      new Date(payment.payment?.completedTimestamp) < new Date(payment.paymentRequest?.discountEndDate)
    ) {
      remainingBalance -= payment.paymentRequest.discountAmount;
    }

    return (
      <Paper elevation={0}>
        <Tabs
          indicatorColor={'primary'}
          textColor={'primary'}
          value={tabValue}
          classes={{ root: classesDesktop.tabsRoot, indicator: classesDesktop.tabsIndicator }}
        >
          <Tab
            classes={{ selected: classesDesktop.selectedTab, root: classesDesktop.tabRoot }}
            label="PAYMENT HISTORY"
            id="payment-history-tab"
            data-cy="payment-history-tab"
          />
        </Tabs>
        <TabPanel value={tabValue} index={0} data-cy="payment-history-panel">
          <TableContainer className={classesDesktop.table}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell className={classesDesktop.headerCell}>Payment Date</TableCell>
                  <TableCell className={classesDesktop.headerCell}>Payment Method</TableCell>
                  <TableCell className={classesDesktop.headerCell} align="right">
                    Paid
                  </TableCell>
                  {creditMemosEnabled && (
                    <>
                      <TableCell className={classesDesktop.headerCell} align="right">
                        Credit Applied
                      </TableCell>
                      <TableCell className={classesDesktop.headerCell} align="right">
                        Total
                      </TableCell>
                    </>
                  )}
                  <TableCell className={classesDesktop.headerCell} align="right">
                    Balance
                  </TableCell>
                  <TableCell className={classesDesktop.headerCell}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payment?.paymentRequest?.payments.map(singlePayment => {
                  if (singlePayment?.amount) {
                    if (singlePayment?.status === PaymentStatus.Completed || singlePayment?.status === PaymentStatus.Pending) {
                      remainingBalance -= singlePayment.amount;
                    }
                  }
                  if (singlePayment?.creditAmount) {
                    remainingBalance -= singlePayment.creditAmount;
                  }

                  const currencyFormatter = new Intl.NumberFormat('en', {
                    style: 'currency',
                    currency: singlePayment?.currency || 'USD',
                    currencyDisplay: 'symbol',
                  });

                  return (
                    <TableRow key={singlePayment?.id}>
                      <TableCell className={classesDesktop.cell}>
                        <Typography>
                          {new Date(singlePayment?.attemptTimestamp || singlePayment?.createdAt).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell className={classesDesktop.cell}>
                        {getPaymentMethodString(singlePayment?.paymentMethod, singlePayment?.amount, singlePayment?.creditAmount)}
                      </TableCell>
                      <TableCell className={classesDesktop.cell} align="right">
                        {currencyFormatter.format(typeof singlePayment?.amount === 'number' ? singlePayment.amount / 100 : 0)}
                      </TableCell>
                      {creditMemosEnabled && (
                        <>
                          <TableCell className={classesDesktop.cell} align="right">
                            {currencyFormatter.format(
                              typeof singlePayment?.creditAmount === 'number' ? singlePayment.creditAmount / 100 : 0,
                            )}
                          </TableCell>
                          <TableCell className={classesDesktop.cell} align="right">
                            {currencyFormatter.format(((singlePayment?.amount || 0) + (singlePayment?.creditAmount || 0)) / 100)}
                          </TableCell>
                        </>
                      )}
                      <TableCell className={classesDesktop.cell} align="right">
                        {currencyFormatter.format(remainingBalance / 100)}
                      </TableCell>
                      <TableCell className={classesDesktop.cell}>
                        <Typography>
                          {singlePayment?.amountDisputed
                            ? 'Disputed'
                            : paymentStatusDisplay.find(item => {
                                return item.name === singlePayment?.status;
                              })?.display}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Paper>
    );
  };

  return (
    <Dialog
      onClose={closeRecentPaymentDetails}
      open={recentPaymentDetailsVisible}
      fullWidth={paymentsHistoryCount || matches}
      maxWidth={'md'}
      classes={{ paper: paymentsHistoryCount || matches ? '' : classesDesktop.dialogWidth }}
      data-cy="recent-payment-details-modal"
    >
      <Box className={classesDesktop.dialogContent}>
        <Box className={classesDesktop.infoSections}>
          <Grid container>
            <Grid item xs={10}>
              <Typography variant="subtitle2">PAYMENT REQUEST</Typography>
              <Typography className={classesDesktop.referenceId}>
                <Tooltip
                  className={classesDesktop.tooltip}
                  title={`${referenceCopied ? 'Copied' : 'Copy'} to clipboard`}
                  placement="bottom-start"
                >
                  <Typography
                    className={classesDesktop.referenceContent}
                    color="primary"
                    onClick={() => {
                      setReferenceCopied(true);
                      navigator.clipboard.writeText(payment?.paymentRequest?.referenceNumber || '');
                    }}
                    onMouseEnter={() => {
                      if (referenceCopied) setReferenceCopied(false);
                    }}
                    data-cy="payment-id-copy"
                  >
                    {payment?.paymentRequest?.referenceNumber}
                    <CopyIcon className={classesDesktop.copyIcon} />
                  </Typography>
                </Tooltip>
              </Typography>
            </Grid>
            <Grid item xs={2} className={classesDesktop.closeButtonContainer}>
              <IconButton
                aria-label="close"
                title="close"
                className={classesDesktop.closeButton}
                onClick={closeRecentPaymentDetails}
                data-cy="recent-payment-details-close"
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Box>
        <Box className={matches ? classesDesktop.contentSectionSm : classesDesktop.contentSectionMd}>
          <Grid container>
            <Grid container item xs={12} md={9}>
              <Grid container spacing={1}>
                {payment?.paymentRequest?.orderNumber && (
                  <>
                    <Grid item xs={6} md={paymentsHistoryCount ? 4 : 6}>
                      <Typography className={classesDesktop.headingContent}>Order#</Typography>
                    </Grid>
                    <Grid item xs={6} md={paymentsHistoryCount ? 8 : 6}>
                      <Typography className={classesDesktop.detailsContent}>{payment.paymentRequest.orderNumber}</Typography>
                    </Grid>
                  </>
                )}
                {payment?.paymentRequest?.customerPONumber && (
                  <>
                    <Grid item xs={6} md={paymentsHistoryCount ? 4 : 6}>
                      <Typography className={classesDesktop.headingContent}>Customer PO#</Typography>
                    </Grid>
                    <Grid item xs={6} md={paymentsHistoryCount ? 8 : 6}>
                      <Typography className={classesDesktop.detailsContent}>{payment.paymentRequest.customerPONumber}</Typography>
                    </Grid>
                  </>
                )}
                {payment?.paymentRequest?.invoiceNumber && (
                  <>
                    <Grid item xs={6} md={paymentsHistoryCount ? 4 : 6}>
                      <Typography className={classesDesktop.headingContent}>Invoice#</Typography>
                    </Grid>
                    <Grid item xs={6} md={paymentsHistoryCount ? 8 : 6}>
                      <Typography className={classesDesktop.detailsContent}>{payment.paymentRequest.invoiceNumber}</Typography>
                    </Grid>
                  </>
                )}

                <Grid item xs={6} md={paymentsHistoryCount ? 4 : 6}>
                  <Typography className={classesDesktop.headingContent}>Request Amount</Typography>
                </Grid>
                <Grid item xs={6} md={paymentsHistoryCount ? 8 : 6}>
                  <Typography className={classesDesktop.detailsContent}>
                    {typeof payment?.paymentRequest?.amount === 'number'
                      ? new Intl.NumberFormat('en', {
                          style: 'currency',
                          currency: payment?.currency || 'USD',
                          currencyDisplay: 'symbol',
                        }).format(payment?.paymentRequest?.amount / 100)
                      : null}
                  </Typography>
                </Grid>

                <Grid item xs={6} md={paymentsHistoryCount ? 4 : 6}>
                  <Typography className={classesDesktop.headingContent}>Requested On</Typography>
                </Grid>
                <Grid item xs={6} md={paymentsHistoryCount ? 8 : 6}>
                  <Typography className={classesDesktop.detailsContent}>
                    {new Date(payment?.paymentRequest?.createdAt).toLocaleDateString()}
                  </Typography>
                </Grid>

                {payment?.paymentRequest?.discountEndDate && (
                  <>
                    <Grid item xs={6} md={paymentsHistoryCount ? 4 : 6}>
                      <Typography className={classesDesktop.headingContent}>Discount End Date</Typography>
                    </Grid>
                    <Grid item xs={6} md={paymentsHistoryCount ? 8 : 6}>
                      <Typography className={classesDesktop.detailsContent}>
                        {new Date(payment.paymentRequest.discountEndDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  </>
                )}

                {payment?.paymentRequest?.dueDate && (
                  <>
                    <Grid item xs={6} md={paymentsHistoryCount ? 4 : 6}>
                      <Typography className={classesDesktop.headingContent}>Final Payment Due</Typography>
                    </Grid>
                    <Grid item xs={6} md={paymentsHistoryCount ? 8 : 6}>
                      <Typography className={classesDesktop.detailsContent}>
                        {new Date(payment?.paymentRequest?.dueDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  </>
                )}

                <Grid item xs={6} md={paymentsHistoryCount ? 4 : 6}>
                  <Typography className={classesDesktop.headingContent}>Status</Typography>
                </Grid>
                <Grid item xs={6} md={paymentsHistoryCount ? 8 : 6}>
                  <Typography className={classesDesktop.detailsContent}>
                    {
                      paymentStatusDisplay.find(item => {
                        return item.name === payment?.paymentRequest?.status;
                      })?.display
                    }
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} md={3} className={matches ? classesDesktop.actionButtonsContainerSm : ''}>
              {payment?.paymentRequest?.invoiceLink && payment?.paymentRequest?.invoiceLink.length > 0 && (
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  size="small"
                  href={payment?.paymentRequest?.invoiceLink}
                  className={classesDesktop.paymentButton}
                  data-cy="download-invoice"
                >
                  DOWNLOAD INVOICE
                </Button>
              )}
            </Grid>
          </Grid>
        </Box>
        {paymentsHistoryCount && <Box className={classesDesktop.infoSections}>{paymentHistory()}</Box>}
      </Box>
    </Dialog>
  );
};

export default RecentPaymentDetails;
